import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CircleAlert, KeyRound, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { ArrivalDepartureItem, CompleteMovementInput } from "@apartos/contracts";
import { useDialog } from "../hooks/useDialog";
import { ModalPortal } from "./ModalPortal";
import { CancelBookingModal } from "./CancelBookingModal";
import { api } from "../lib/api";

export function MovementModal({ item, onClose, onCollectPayment }: {
  item: ArrivalDepartureItem;
  onClose: () => void;
  onCollectPayment: (bookingId: string) => void;
}) {
  const dialogRef = useDialog<HTMLElement>(onClose);
  const client = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState<CompleteMovementInput>({
    direction: item.direction,
    documentsVerified: item.documentsReady,
    keyIssued: item.direction === "arrival",
    depositAmount: 0,
    timing: item.status === "late" ? "late" : "regular",
  });
  const mutation = useMutation({
    mutationFn: () => api.completeMovement(item.id, form),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["movements"] }),
        client.invalidateQueries({ queryKey: ["bookings"] }),
        client.invalidateQueries({ queryKey: ["apartments"] }),
        client.invalidateQueries({ queryKey: ["housekeeping"] }),
        client.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
      onClose();
    },
  });
  const [cancelOpen, setCancelOpen] = useState(false);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };
  const blockers = item.direction === "arrival"
    ? arrivalBlockers(item, Boolean(form.documentsVerified))
    : departureBlockers(item);

  return <><ModalPortal onClose={onClose}>
    <section ref={dialogRef} className="booking-modal movement-modal" role="dialog" aria-modal="true" aria-labelledby="movement-modal-title">
      <header className="modal-header">
        <div><h2 id="movement-modal-title">{item.direction === "arrival" ? "Оформить заселение" : "Оформить выезд"}</h2><p>{item.guestName} · Apt {item.apartmentNumber}</p></div>
        <button className="icon-button" onClick={onClose} aria-label="Закрыть"><X size={17} /></button>
      </header>
      <form onSubmit={submit}>
        <div className="modal-body">
          <div className="movement-summary"><div className="mini-avatar">{item.guestInitials}</div><div><strong>{item.guestName}</strong><span>{item.phone} · {item.guests} гостя</span></div><b>Apt {item.apartmentNumber}</b></div>
          {item.status === "late" && <div className="movement-overdue-banner"><CircleAlert size={17} /><div><strong>{item.direction === "arrival" ? "Просроченный заезд" : "Просроченный выезд"}</strong><span>{item.scheduledAt ? new Date(item.scheduledAt).toLocaleString("ru-RU", { timeZone: "Asia/Tashkent", dateStyle: "medium", timeStyle: "short" }) : item.time}{item.overdueDays ? ` · ${item.overdueDays} дн.` : ""}</span></div></div>}
          {item.direction === "arrival" && <div className="readiness">
            <span className={item.paymentReady ? "text-green" : "text-red"}>{item.paymentReady ? "Оплата закрыта" : "Есть задолженность"}</span>
            <span className={item.documentsReady || form.documentsVerified ? "text-green" : "text-red"}>{item.documentsReady || form.documentsVerified ? "Документы подтверждены" : "Нужно проверить документ"}</span>
            <span className={item.roomReady ? "text-green" : "text-red"}>{item.roomReady ? "Номер готов" : "Номер не готов"}</span>
          </div>}
          {item.direction === "departure" && <div className="readiness">
            <span className={item.paymentReady ? "text-green" : "text-red"}>{item.paymentReady ? "Счёт закрыт" : "Есть задолженность"}</span>
            <span className="text-green">Выезд будет зафиксирован</span>
            <span>После выезда создастся новая уборка</span>
          </div>}
          {item.direction === "arrival" && blockers.length > 0 && <div className="movement-blockers" role="status">
            <div><CircleAlert size={16} /><span><strong>Заселение пока заблокировано</strong><small>{blockers.join(" · ")}</small></span></div>
            <div>
              {!item.paymentReady && <button type="button" className="secondary-button" onClick={() => onCollectPayment(movementBookingId(item.id))}>Провести оплату</button>}
              {!item.roomReady && <button type="button" className="secondary-button" onClick={() => { onClose(); navigate("/apartments"); }}>Проверить апартамент</button>}
            </div>
          </div>}
          {item.direction === "departure" && blockers.length > 0 && <div className="movement-blockers" role="status">
            <div><CircleAlert size={16} /><span><strong>Выезд пока заблокирован</strong><small>{blockers.join(" · ")}</small></span></div>
            <div><button type="button" className="secondary-button" onClick={() => onCollectPayment(movementBookingId(item.id))}>Провести оплату</button></div>
          </div>}
          <div className="form-grid">
            <label className="field"><span>Время операции</span><select name="timing" value={form.timing} onChange={(event) => setForm({ ...form, timing: event.target.value as CompleteMovementInput["timing"] })}><option value="regular">По расписанию</option><option value="early">Ранний {item.direction === "arrival" ? "заезд" : "выезд"}</option><option value="late">Поздний {item.direction === "arrival" ? "заезд" : "выезд"}</option></select></label>
            <label className="field"><span>Депозит, $</span><input name="depositAmount" type="number" inputMode="decimal" min="0" step="0.01" value={form.depositAmount ?? 0} onChange={(event) => setForm({ ...form, depositAmount: Number(event.target.value) })} /></label>
            {item.direction === "arrival" && <label className="consent-field field--wide"><input name="documentsVerified" type="checkbox" checked={form.documentsVerified ?? false} onChange={(event) => setForm({ ...form, documentsVerified: event.target.checked })} /><span>Документ гостя проверен</span></label>}
            <label className="consent-field field--wide"><input name="keyIssued" type="checkbox" checked={form.keyIssued ?? false} onChange={(event) => setForm({ ...form, keyIssued: event.target.checked })} /><KeyRound size={15} /><span>{item.direction === "arrival" ? "Ключ выдан гостю" : "Ключ возвращён"}</span></label>
          </div>
          {mutation.error && <div className="form-error" role="alert"><CircleAlert size={15} />{mutation.error?.message}</div>}
        </div>
        <footer className="modal-footer">
          {item.direction === "arrival" && <button type="button" className="danger-button" onClick={() => setCancelOpen(true)}>Отменить бронь</button>}
          <div className="modal-footer__spacer" />
          <button type="button" className="secondary-button" onClick={onClose}>Закрыть</button>
          <button className="primary-button" disabled={mutation.isPending || blockers.length > 0}>{mutation.isPending ? "Проводим…" : item.direction === "arrival" ? "Заселить" : "Завершить выезд"}</button>
        </footer>
      </form>
    </section>
  </ModalPortal>
  {cancelOpen && <CancelBookingModal booking={{ id: movementBookingId(item.id), guestName: item.guestName, apartmentNumber: item.apartmentNumber }} onClose={() => setCancelOpen(false)} onDone={onClose} />}</>;
}

export function movementBookingId(movementId: string): string {
  return movementId.replace(/_(arrival|departure)$/, "");
}

export function arrivalBlockers(item: ArrivalDepartureItem, documentsVerified: boolean): string[] {
  if (item.direction !== "arrival") return [];
  return [
    ...(item.periodExpired ? ["срок бронирования истёк — измените даты с причиной или оформите no-show"] : []),
    ...(!item.paymentReady ? ["нужно закрыть оплату"] : []),
    ...(!documentsVerified ? ["нужно проверить документ"] : []),
    ...(!item.roomReady ? ["апартамент должен быть свободен и готов"] : []),
  ];
}

export function departureBlockers(item: ArrivalDepartureItem): string[] {
  if (item.direction !== "departure") return [];
  return item.paymentReady ? [] : ["нужно закрыть остаток по счёту гостя"];
}
